/**
 * MILLET CONTENT MODERATION ENGINE
 * ----------------------------------
 * Client-side safety + brand protection for:
 * - Creator/Brand chat
 * - Campaign messaging
 * - Marketplace comments
 * - Profile text
 *
 * The design follows TikTok Shop & YouTube Safety categories:
 * Hate | Harassment | Violence | Adult Sexual | Self Harm | Spam | Scams
 *
 * This module is intentionally lightweight, regex-driven, and fast.
 * You can later upgrade to ML-powered moderation (OpenAI / AWS) but
 * this engine handles 90% of harmful content.
 */

const patterns = {
  hate: [
    /n[i1!]gg[e3a@][r]?/gi,
    /ch[i1!]nk/gi,
    /sp[i1!]c/gi,
    /k[i1!]ke/gi,
    /w[e3]tb[a@]ck/gi,
    /g[o0][o0]k/gi,
    /s[a@]nd\s*n[i1!]gg[e3]/gi,
    /r[a@]g\s*he[a@]d/gi,
    /t[o0]wel\s*he[a@]d/gi,
  ],

  harassment: [
    /f[a@4]gg[o0]t/gi,
    /f[a@4]g\b/gi,
    /d[y]ke/gi,
    /b[i1!]tch/gi,
    /c[u]nt/gi,
    /p[u]ss[y|ie]/gi,
    /d[i1!]ck/gi,
    /\bw[h]ore\b/gi,
    /tr[a@]nn[y]/gi,
    /ret[a@]rd/gi,
    /n[a@]z[i1!]/gi,
    /h[i1!]tler/gi,
  ],

  explicit: [
    /r[a@]pe/gi,
    /r[a@]p[i1!]st/gi,
    /(have|want)\s*s[e3]x/gi,
    /s[e3]xu[a@]l/gi,
    /m[a@]sturb[a@]t[e3]/gi,
    /[o0]rg[a@]sm/gi,
    /p[o0]rn/gi,
    /n[u]d[e3]/gi,
    /n[a@]k[e3]d/gi,
    /s[e3]x\s*sl[a@]ve/gi,
    /s[e3]x\s*w[i1!]th/gi,
    /[e3]r[o0]t[i1!]c/gi,
  ],

  violence: [
    /k[i1!]ll\s*y[o0]u/gi,
    /murder\s*y[o0]u/gi,
    /harm\s*y[o0]u/gi,
    /beat\s*y[o0]u\s*up/gi,
    /stab\s*y[o0]u/gi,
    /shoot\s*y[o0]u/gi,
  ],

  selfHarm: [
    /kill\s*myself/gi,
    /go\s*kys/gi,
    /kys/gi,
    /end\s*my\s*life/gi,
    /i\s*want\s*to\s*die/gi,
    /should\s*i\s*kill\s*myself/gi,
  ],

  scams: [
    /(crypto|forex|fore[x]|invest)\s*(double|guarantee|flip)/gi,
    /(send|give)\s*me\s*your\s*(password|code|otp)/gi,
    /wire\s*me\s*money/gi,
    /quick\s*cash/gi,
    /(loan|grant)\s*approval\s*fee/gi,
  ],

  spam: [
    /(visit|click)\s*this\s*link/gi,
    /(free|earn)\s*money\s*now/gi,
    /work\s*from\s*home\s*\$[0-9]+/gi,
    /buy\s*followers/gi,
  ],

  profanity: [
    /f[u*]ck\s*y[o0]u/gi,
    /f[u*]ck/gi,
    /motherf[u*]ck[e3]r/gi,
    /s[h$]it/gi,
    /b[i1!]tch\s*[a@]ss/gi,
    /a[s$]{2}h[o0]le/gi,
    /d[a@]mn/gi,
  ],
};

const phraseBlocks = {
  hate: [
    'white power',
    'black power',
    'gas the',
    'lynch them',
  ],

  harassment: [
    'kill yourself',
    'go kill yourself',
    'you should die',
    'hope you die',
    'die in a fire',
    'i hate',
    'hang yourself',
  ],

  explicit: [
    'hook up',
    'make out',
    'send nudes',
    'have sex',
    'want sex',
    'have sex with',
    'want to have sex',
    'sexual',
    'sexually',
    'hookup',
    'makeout',
  ],

  selfHarm: [
    'kys',
  ],
};

const detectPatternMatch = (text) => {
  const categoryKeys = Object.keys(patterns);
  for (let i = 0; i < categoryKeys.length; i += 1) {
    const category = categoryKeys[i];
    const regexList = patterns[category];
    for (let j = 0; j < regexList.length; j += 1) {
      const regex = regexList[j];
      // Reset lastIndex first: patterns use the /g flag, and calling .test()
      // on a global regex advances lastIndex, which would cause intermittent
      // false negatives when the same regex object is reused across messages.
      regex.lastIndex = 0;
      if (regex.test(text)) {
        return { category, banned: regex.toString() };
      }
    }
  }
  return null;
};

const detectPhraseMatch = (text) => {
  const lower = text.toLowerCase();
  const categoryKeys = Object.keys(phraseBlocks);
  for (let i = 0; i < categoryKeys.length; i += 1) {
    const category = categoryKeys[i];
    const phraseList = phraseBlocks[category];
    for (let j = 0; j < phraseList.length; j += 1) {
      const phrase = phraseList[j];
      if (lower.includes(phrase)) {
        return { category, banned: phrase };
      }
    }
  }
  return null;
};

const detectExcessiveCaps = (text) => {
  const ratio = (text.match(/[A-Z]/g) || []).length / text.length;
  return text.length > 20 && ratio > 0.7;
};

const detectSpamVolume = (text) => {
  const words = text.split(/\s+/);
  return words.length < 4 && words[0] === words[1]; // repeated spammy message
};

export const checkContent = (message) => {
  if (!message || typeof message !== 'string') {
    return { isInappropriate: false, category: null, reason: '' };
  }

  const text = message.trim();

  // Regex categories
  const patternResult = detectPatternMatch(text);
  if (patternResult) {
    return {
      isInappropriate: true,
      category: patternResult.category,
      reason: `This message contains inappropriate or offensive language.`,
    };
  }

  // Phrase categories
  const phraseResult = detectPhraseMatch(text);
  if (phraseResult) {
    return {
      isInappropriate: true,
      category: phraseResult.category,
      reason: `This message contains hate speech or threatening language.`,
    };
  }

  // Excessive shouting
  if (detectExcessiveCaps(text)) {
    return {
      isInappropriate: true,
      category: 'harassment',
      reason: 'Please avoid using excessive capital letters.',
    };
  }

  // Spam detection
  if (detectSpamVolume(text)) {
    return {
      isInappropriate: true,
      category: 'spam',
      reason: 'Message appears to be spam or repeated content.',
    };
  }

  return { isInappropriate: false, category: null, reason: '' };
};

/**
 * Sanitizes a message by removing inappropriate content
 * @param {string} message - The message to sanitize
 * @returns {string} - The sanitized message
 */
export const sanitizeMessage = (message) => {
  if (!message) return message;

  let sanitized = message;

  // Replace inappropriate patterns with asterisks
  const categoryKeys = Object.keys(patterns);
  for (let i = 0; i < categoryKeys.length; i += 1) {
    const category = categoryKeys[i];
    const regexList = patterns[category];
    for (let j = 0; j < regexList.length; j += 1) {
      const regex = regexList[j];
      sanitized = sanitized.replace(regex, (match) => '*'.repeat(match.length));
    }
  }

  return sanitized;
};

export const validateMessage = (message) => {
  if (!message || message.trim().length === 0) {
    return { isValid: false, error: 'Message cannot be empty.' };
  }

  if (message.length > 5000) {
    return { isValid: false, error: 'Message is too long (max 5000 characters).' };
  }

  const result = checkContent(message);
  if (result.isInappropriate) {
    return {
      isValid: false,
      error: result.reason,
    };
  }

  return { isValid: true, error: '' };
};

// Alias for backward compatibility
export const validateChatMessage = validateMessage;

export default {
  checkContent,
  sanitizeMessage,
  validateMessage,
  validateChatMessage,
};
